import s from './TagsView.module.scss';
import { useCreateTagMutation, useDeleteTagMutation, useGetTagsQuery, useUpdateTagMutation } from '../../redux/apis/tagsApi';
import { useEffect, useState } from 'react';
import { useStatusContext } from 'components/StatusProvider';
import { useTranslation } from 'react-i18next';
import { Tabs } from 'components/Tabs/Tabs';
import { ReactComponent as PlusIcon } from "../../assets/icons/plus.svg"
import { Modal } from 'components/Modals/Modal/Modal';
import { ColorPicker } from 'components/ColorPicker/ColorPicker';
import { PrimaryButton } from 'components/PrimaryButton/PrimaryButton';
import { SecondaryButton } from 'components/SecondaryButton/SecondaryButton';
import { Skeleton } from 'components/Skeleton/Skeleton';
import { ReactComponent as DeleteIcon } from "../../assets/icons/delete_icon.svg"
import { ReactComponent as EditIcon } from "../../assets/icons/edit_icon.svg"
import { ModalGeneral } from 'components/Modals/ModalGeneral';
import questionMark from "assets/images/questionMark.webp";

type TagsViewType = {
    type?: string
    currentTags?: any
    setCurrentTag?: any
}

export const TagsView = ({type, setCurrentTag}: TagsViewType) => {

    // ### State
    const [showDeleteModal, setShowDeleteModal] = useState<any>(false);
    const [showTagModal, setShowTagModal] = useState<any>(false);
    const [showTagsModal, setShowTagsModal] = useState<any>(false);
    const [tabItems, setTabItems] = useState<any>()
    const [tab, setTab] = useState<any>([])
    const [tag, setTag] = useState<any>({})
    const { openStatus } = useStatusContext();
    const {t} = useTranslation()

    const [DeleteTag, {isLoading: isDeleteTagLoading}] = useDeleteTagMutation()
    const [UpdateTag, {isLoading: isUpdateTagLoading}] = useUpdateTagMutation()
    const [CreateTag, {isLoading: isCreateTagLoading}] = useCreateTagMutation()
    const {data: tags, isLoading} = useGetTagsQuery({type})
    

    // ### Effects

    useEffect(()=>{
        if(tab) setCurrentTag(tags?.[tab - 1]?.name)
        else setCurrentTag(undefined)
    },[tab]);

    useEffect(()=>{
        if(tags?.length){
            setTabItems([
                {
                    id: 0,
                    title: "All"
                },
                ...tags?.map((tag: any) => {
                    return{
                        id: tag.id,
                        title: tag.name,
                        editItem: () => alert(`edit ${tag.id}`),
                        deleteItem: () => alert(`delete ${tag.id}`)
                    }
                })
            ])
            setTab(0)
            setCurrentTag(undefined)
        }
    },[tags]);

    // ### Functions
    async function handleCreateTag(){
        const action = tag?.id ? UpdateTag : CreateTag
        const response: any = await action({id: tag?.id, name: tag.name, type, color: tag.color})
        if(response.data?.message === "tag_created" || response.data?.message === "tag_updated"){
            openStatus('success', t(`courses.${response.data.message}`))
            setShowTagModal(undefined)
            setTag({})
        }
        else openStatus('error', response.error?.data?.message)
    }

    async function handleDelete(id: any){
        const response: any = await DeleteTag({id})
        if(response.data?.message === "tag_deleted"){
            openStatus('success', t(`status.success`))
        }
        else openStatus('error', response.error?.data?.message)
    }

    // ### Views
    const tagsView = tags?.map((tag: any, index: number) => (
        <div key={index} className={s.tag_row}>
            <p className={s.title}><span className={s.color} style={{background: tag.color}}></span> {tag.name}</p>
            <div 
                className={s.menu_wrapper} 
                onClick={() => {
                    setShowTagModal(tag)
                    setTag(tag)
                }}
            ><EditIcon /></div>
            <div className={s.menu_wrapper} onClick={() => setShowDeleteModal(tag.id)}><DeleteIcon /></div>
        </div>
    ))

    return(
        <div className={s.tabs_wrapper}>
            {
                tags?.length && tabItems?.length ? 
                <Tabs 
                    items={tabItems}
                    tab={tab}
                    setTab={(value) => setTab(value)}
                /> : null
            }
            <div className={s.button_wrapper} onClick={() => setShowTagModal({})}>
                <PlusIcon/>
                <span className={s.tag_button}>
                    {t('blog.add_tag')}
                </span>
            </div>
            <div className={s.edit_wrapper} onClick={() => setShowTagsModal(true)}>
                <EditIcon/>
            </div>

            <Modal show={showTagModal} setShow={()=>setShowTagModal(undefined)} style={{zIndex: 150}}>
                <div className={s.add_modal}>
                    <p className={s.modal_title}>{t('blog.add_tag')}</p>
                    <div className={s.row}>
                        <span className={s.title}>{t('general.name')}</span>
                        <input 
                            type="text"
                            value={tag?.name || ''}
                            onChange={(ev: any) => setTag((prev: any) => ({...prev, name: ev.target.value}))}
                        />
                    </div>
                    <div className={s.row}>
                        <span className={s.title}>{t('general.color')}</span>
                        <ColorPicker color={tag.color} setColor={(color: any) => setTag((prev: any) => ({...prev, color}))}/>
                    </div>
                    <div className={s.buttons}>
                        <PrimaryButton 
                            type={"button"}
                            text={t("general.save")}
                            onClick={handleCreateTag}
                            style={{width: "fit-content"}}
                            disabled={isCreateTagLoading}
                        />
                        <SecondaryButton 
                            type={"button"}
                            text={t('general.back')}
                            onClick={() => {
                                setTag({})
                                setShowTagModal(undefined)
                            }}
                            style={{width: "fit-content"}}
                        />
                    </div>
                </div>
            </Modal>

            <Modal show={showTagsModal} setShow={()=>setShowTagsModal(false)}>
                <div className={s.add_modal}>
                    <p className={s.modal_title}>{t('blog.tag_list')}</p>
                    {tagsView}
                </div>
            </Modal>

            <Skeleton isLoading={isLoading || isDeleteTagLoading || isUpdateTagLoading} />

            <ModalGeneral
                showModal={showDeleteModal}
                setShowModal={setShowDeleteModal}
                title={t("general.delete")}
                textLeftButton={t("general.cancel")}
                textRightButton={t("general.delete")}
                image={questionMark}
                description={`${t("general.areYouSure")}? ${t("general.itsContents")}`}
                buttonRight={() => handleDelete(showDeleteModal)}
            />
        </div>
    )
}